// The fonts of a dashboard's text roles (plan 2026-10-16-dashboard-font-
// choices, D4). The server resolves dashboard -> account -> platform and
// sends `fonts` on the dashboard DTO; a block's own `font_*` overrides it.
// That last step is CSS: the canvas root sets `--dash-font-<role>` from the
// dashboard's fonts, a tile wrapper sets the same variables from the
// block's own, and the cascade lets the block win.
//
// Neutral on purpose (not under `components/dashboards/`): the shared chart
// renderers (MetricTable, PivotTableRenderer, MetricCardRenderer) read these
// names and must not import a dashboards module. Outside a dashboard no
// variable is set, and `font-family: var(--x)` with no fallback computes to
// `unset`, which is `inherit` for font-family: chat and Insights keep their
// font.
//
// No string from input reaches CSS: a key is a lookup in the emitted
// catalogue (own keys only), and the stack it yields is checked again with
// the sink pattern before it is used.
import { createContext, type CSSProperties } from "react";
import { REPORT_FONTS } from "@verity/shared-types/report-fonts";
import { contrastRatio } from "@verity/charts";

export const FONT_ROLES = [
  "title",
  "text",
  "chart_title",
  "chart_subtitle",
  "table_header",
  "table_body",
  "kpi_value",
  // A control block's label and its values (plan 2026-10-19-control-block-
  // styling, D1). `control_value` takes no colour: its colour is per state
  // (`controlStyle.ts`).
  "control_label",
  "control_value",
] as const;
export type FontRole = (typeof FONT_ROLES)[number];

/** A row's own text styles as a DTO carries them (`text_styles`, plan
 *  2026-10-17-dashboard-text-style-complete): per role, the attributes that
 *  level sets. Every reader below re-checks each value before a style uses it. */
export type ItemTextStyles = { text_styles?: Partial<Record<FontRole, Record<string, unknown> | null | undefined>> | null };
/** The block's own font, colour and size are read from the same field. */
export type ItemFontFields = ItemTextStyles;
/** The dashboard's effective fonts (null = Verity's default). */
export type DashboardFonts = Partial<Record<FontRole, string | null>>;

/** The CSS custom property a role is drawn with. */
export function fontVar(role: FontRole): `--dash-font-${string}` {
  return `--dash-font-${role.replace(/_/g, "-")}`;
}

/** `font-family` for an element that otherwise inherits: no fallback, so
 *  an unset variable is plain inheritance. */
export function inheritedFontFamily(role: FontRole): string {
  return `var(${fontVar(role)})`;
}

export type CatalogueFont = { key: string; family: string; classification: "serif" | "sans"; stack: string };

// Same pattern as the report brand's sink (`report-components/src/brand.ts`).
const FONT_STACK = /^[A-Za-z0-9 ,'"-]+$/;

export const DASHBOARD_FONTS: readonly CatalogueFont[] = REPORT_FONTS.fonts.map((f) => ({
  key: f.key,
  family: f.family,
  classification: f.classification as CatalogueFont["classification"],
  stack: f.stack,
}));

const STACK_BY_KEY: ReadonlyMap<string, string> = new Map(
  DASHBOARD_FONTS.filter((f) => FONT_STACK.test(f.stack)).map((f) => [f.key, f.stack]),
);

/** The family name to show for a key, or null for an unknown one. */
export function fontFamilyName(key: string | null | undefined): string | null {
  return DASHBOARD_FONTS.find((f) => f.key === key)?.family ?? null;
}

/** The CSS stack for a catalogue key, or null for null / an unknown key. */
export function fontStack(key: unknown): string | null {
  return typeof key === "string" ? (STACK_BY_KEY.get(key) ?? null) : null;
}

/** The families that also have a 500 face in the app: Inter and Plus
 *  Jakarta Sans, because `index.html` loads them from Google Fonts with
 *  500 (and same-name `@font-face` rules merge). The catalogue itself ships
 *  400/600/700 only. A test reads `index.html`, so this cannot go stale. */
export const FAMILIES_WITH_500: ReadonlySet<string> = new Set(["inter", "plus-jakarta-sans"]);

/** The weight a medium (500) label in this role draws at, set next to the
 *  font itself so the two always come from the same level. */
export function fontMediumVar(role: FontRole): `--dash-font-${string}-medium` {
  return `${fontVar(role)}-medium`;
}

/** `font-weight` for a medium (500) label: 600 when the role's family has
 *  no 500 face (the browser would draw 400), else 500; 500 outside a
 *  dashboard. */
export function mediumFontWeight(role: FontRole): string {
  return `var(${fontMediumVar(role)}, 500)`;
}

/** The variables to set for the roles given. A null or unknown key sets
 *  nothing, so that role follows the level above (or inherits). */
export function fontVariables(fonts: DashboardFonts | null | undefined): CSSProperties {
  const style: Record<string, string> = {};
  if (!fonts) return style;
  for (const role of FONT_ROLES) {
    const key = fonts[role];
    const stack = fontStack(key);
    if (!stack) continue;
    style[fontVar(role)] = stack;
    style[fontMediumVar(role)] = FAMILIES_WITH_500.has(key as string) ? "500" : "600";
  }
  return style;
}

/** The roles a block sets a font for itself, from its `text_styles`. */
export function itemFonts(item: ItemFontFields | null | undefined): DashboardFonts {
  const out: DashboardFonts = {};
  if (!item) return out;
  for (const role of FONT_ROLES) {
    const key = item.text_styles?.[role]?.font;
    if (fontStack(key)) out[role] = key as string;
  }
  return out;
}

/** The effective key per role for one block: its own, else the dashboard's. */
export function effectiveFonts(dashboard: DashboardFonts | null | undefined, item: ItemFontFields | null | undefined): DashboardFonts {
  const own = itemFonts(item);
  const out: DashboardFonts = {};
  for (const role of FONT_ROLES) {
    const key = own[role] ?? (fontStack(dashboard?.[role]) ? dashboard?.[role] : null);
    if (key) out[role] = key;
  }
  return out;
}

// --- text colours (plan 2026-10-17-dashboard-font-colours) -------------------
//
// The same roles, levels and cascade as the fonts: the canvas root sets
// `--dash-color-<role>` from the dashboard's effective colours, a tile wrapper
// the block's own, and each role's DRAWING element reads
// `var(--dash-color-<role>, <its own default>)` — an explicit fallback, because
// a role's default differs per leaf (a KPI label is dimmed, a title is not).
//
// A stored value is a token (`text` | `muted`, adapts to dark mode), a theme
// slot `palette_1..8` (an index into the dashboard's theme colours) or a
// lowercase `#rrggbb`. A slot or hex is literal and so applies in the LIGHT
// colour scheme only (D2): in dark mode it sets nothing and the role keeps its
// token. Every value is re-checked here before it reaches a style object.

/** The adaptive tokens and the CSS colour each draws with. Equal to
 *  `api/constants/dashboard_font_colors.py` FONT_COLOR_TOKENS (a test). */
export const FONT_COLOR_TOKENS: Readonly<Record<string, string>> = {
  text: "var(--mantine-color-text)",
  muted: "var(--mantine-color-dimmed)",
};
/** The stored grammar, equal to the backend's FONT_COLOR_PATTERN (a test). */
export const FONT_COLOR_PATTERN = /^(text|muted|#[0-9a-f]{6}|palette_[1-8])$/;
const HEX6 = /^#[0-9a-f]{6}$/;
const SLOT = /^palette_([1-8])$/;

/** A block's own colours: its `text_styles`. */
export type ItemFontColorFields = ItemTextStyles;
/** Effective colours per role (null = the role's own default). */
export type DashboardFontColors = Partial<Record<FontRole, string | null>>;

/** The CSS custom property a role's colour is drawn with. */
export function fontColorVar(role: FontRole): `--dash-color-${string}` {
  return `--dash-color-${role.replace(/_/g, "-")}`;
}

/** `color` for a role's drawing element: the dashboard's or block's colour,
 *  else `fallback` (the leaf's own colour today). Without a fallback an unset
 *  variable is invalid at computed-value time, which for the inherited
 *  `color` property means plain inheritance — the leaf draws as today. */
export function roleColor(role: FontRole, fallback?: string): string {
  return fallback ? `var(${fontColorVar(role)}, ${fallback})` : `var(${fontColorVar(role)})`;
}

/** A value in the stored grammar, or null. */
export function storedFontColor(value: unknown): string | null {
  return typeof value === "string" && FONT_COLOR_PATTERN.test(value) ? value : null;
}

/** The hex a stored value draws with in light mode, or null for a token, an
 *  invalid value, or a slot the theme does not have. A slot is an index,
 *  never a lookup by name. */
export function literalFontColor(value: unknown, themeColors: readonly string[] | null | undefined): string | null {
  const v = storedFontColor(value);
  if (!v || v in FONT_COLOR_TOKENS) return null;
  const slot = SLOT.exec(v);
  if (slot) {
    const hex = themeColors?.[Number(slot[1]) - 1];
    return typeof hex === "string" && HEX6.test(hex) ? hex : null;
  }
  return v;
}

/** The CSS colour for a stored value in this scheme, or null (set nothing). */
export function fontColorCss(value: unknown, themeColors: readonly string[] | null | undefined, scheme: "light" | "dark"): string | null {
  const v = storedFontColor(value);
  if (!v) return null;
  if (v in FONT_COLOR_TOKENS) return FONT_COLOR_TOKENS[v];
  return scheme === "light" ? literalFontColor(v, themeColors) : null;
}

/** The colour variables to set for the roles given. */
export function fontColorVariables(
  colors: DashboardFontColors | null | undefined,
  themeColors: readonly string[] | null | undefined,
  scheme: "light" | "dark",
): CSSProperties {
  const style: Record<string, string> = {};
  if (!colors) return style;
  for (const role of FONT_ROLES) {
    const css = fontColorCss(colors[role], themeColors, scheme);
    if (css) style[fontColorVar(role)] = css;
  }
  return style;
}

/** The roles a block sets a colour for itself, from its `text_styles`. */
export function itemFontColors(item: ItemFontColorFields | null | undefined): DashboardFontColors {
  const out: DashboardFontColors = {};
  if (!item) return out;
  for (const role of FONT_ROLES) {
    const v = storedFontColor(item.text_styles?.[role]?.color);
    if (v) out[role] = v;
  }
  return out;
}

// Readability (D3): a WARNING, never a refusal. One rule with the backend's
// `services.dashboard_font_colors.font_color_warning`, pinned to the shared
// vectors in `packages/shared-types/src/fixtures/`.

export const MIN_CONTRAST_NORMAL = 4.5;
export const MIN_CONTRAST_LARGE = 3.0;
const LARGE_TEXT_ROLES: ReadonlySet<FontRole> = new Set<FontRole>(["title"]);

function rgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

export type FontColorWarning = { role: FontRole; colour: string; ratio: number };

/** The warning for one role's colour against the account's page and block
 *  colour, or null when it reads (or adapts). `ratio` is the lower of the
 *  two, rounded DOWN to one decimal so 4.49 never reads as "4.5". */
export function fontColorWarning(
  role: FontRole,
  value: unknown,
  themeColors: readonly string[] | null | undefined,
  pageBg: string,
  blockBg: string,
): FontColorWarning | null {
  const hex = literalFontColor(value, themeColors);
  if (!hex || !HEX6.test(pageBg) || !HEX6.test(blockBg)) return null;
  const ratio = Math.min(contrastRatio(rgb(hex), rgb(pageBg)), contrastRatio(rgb(hex), rgb(blockBg)));
  const floor = LARGE_TEXT_ROLES.has(role) ? MIN_CONTRAST_LARGE : MIN_CONTRAST_NORMAL;
  if (ratio >= floor) return null;
  return { role, colour: value as string, ratio: Math.floor(ratio * 10) / 10 };
}

// --- text sizes (plan 2026-10-17-dashboard-font-sizes-and-text-block-panel) ---
//
// A stored size is the role's MAIN size in px (plan 2026-10-17-dashboard-
// text-style-complete, amendment A1; it replaced ADR-0464's % steps). The
// canvas root and the grid cell set `--dash-size-<role>` to px / today's px
// (a multiplier, "1.5"), so the block wins in the cascade and everything a
// role draws keeps its proportions (a pivot's 11px spans, a compact KPI, em
// headings in markdown): each drawing element reads
// `calc(<today's size> * var(--dash-size-<role>, 1))`. The KPI value is sized in
// JS (it also centres the comparison rows and feeds the fit): the px is its
// size on a full-width tile, and it still shrinks to fit a narrow one.

/** The range, equal to `SIZE_MIN_PX`/`SIZE_MAX_PX` in the backend (a test). */
export const SIZE_MIN_PX = 10;
export const SIZE_MAX_PX = 96;
/** Today's main size per role in px, equal to the backend's `ROLE_BASE_PX`
 *  (a test reads the Python constant). */
export const ROLE_BASE_PX: Readonly<Record<FontRole, number>> = {
  title: 22,
  text: 14,
  chart_title: 14,
  chart_subtitle: 12,
  table_header: 16,
  table_body: 13,
  kpi_value: 32,
  control_label: 14,
  control_value: 12,
};
/** The sizes the editor offers; any whole px in the range is valid. */
export const FONT_SIZE_OPTIONS = [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 26, 28, 32, 36, 40, 48, 56, 64, 72, 80, 96] as const;

/** A role's px as the multiplier its drawing elements read. */
export function sizeMultiplier(role: FontRole, px: number): number {
  return px / ROLE_BASE_PX[role];
}

/** A block's own sizes: its `text_styles`. */
export type ItemFontSizeFields = ItemTextStyles;
/** Effective sizes per role (null = today's size). */
export type DashboardFontSizes = Partial<Record<FontRole, number | null>>;

/** A whole px in the range, or null. */
export function storedFontSize(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= SIZE_MIN_PX && value <= SIZE_MAX_PX ? value : null;
}

/** The CSS custom property a role's size multiplier is drawn with. */
export function fontSizeVar(role: FontRole): `--dash-size-${string}` {
  return `--dash-size-${role.replace(/_/g, "-")}`;
}

/** `font-size` for a role's drawing element: today's size times the step. */
export function roleSize(role: FontRole, base: string): string {
  return `calc(${base} * var(${fontSizeVar(role)}, 1))`;
}

/** The size variables to set for the roles given; only a step sets one. */
export function fontSizeVariables(sizes: DashboardFontSizes | null | undefined): CSSProperties {
  const style: Record<string, string> = {};
  if (!sizes) return style;
  for (const role of FONT_ROLES) {
    const px = storedFontSize(sizes[role]);
    // `calc(px / base)`, not a rounded decimal: the browser keeps full
    // precision, so 32px on a 22px heading draws 32px, not 31.999px.
    if (px !== null) style[fontSizeVar(role)] = `calc(${px} / ${ROLE_BASE_PX[role]})`;
  }
  return style;
}

/** The roles a block sets a size for itself, from its `text_styles`. */
export function itemFontSizes(item: ItemFontSizeFields | null | undefined): DashboardFontSizes {
  const out: DashboardFontSizes = {};
  if (!item) return out;
  for (const role of FONT_ROLES) {
    const step = storedFontSize(item.text_styles?.[role]?.size);
    if (step !== null) out[role] = step;
  }
  return out;
}

/** The effective step per role for one block: its own, else the dashboard's.
 *  The same resolution the CSS cascade does, for the one role sized in JS. */
export function effectiveFontSizes(dashboard: DashboardFontSizes | null | undefined, item: ItemFontSizeFields | null | undefined): DashboardFontSizes {
  const own = itemFontSizes(item);
  const out: DashboardFontSizes = {};
  for (const role of FONT_ROLES) {
    const step = own[role] ?? storedFontSize(dashboard?.[role]);
    if (step !== null && step !== undefined) out[role] = step;
  }
  return out;
}

/** The KPI value's size step as a multiplier (1 = today's), for the shared
 *  KPI renderer: a dashboard tile provides it, every other surface gets 1,
 *  so the renderer imports nothing from dashboards (ADR-0450 §4). */
export const ValueSizeStepContext = createContext(1);

/** The KPI value's effective weight and italic as a key ("700|0"): they
 *  change the value's width, so the renderer's fit re-measures when they
 *  change (plan 2026-10-17-dashboard-text-style-complete). Empty outside a
 *  dashboard. The weight itself is CSS (`--dash-weight-kpi-value`). */
export const ValueStyleKeyContext = createContext("");

// --- the rest of the text style (plan 2026-10-17-dashboard-text-style-complete) -
//
// Weight, italic, underline, alignment, letter case and line spacing: the same
// levels and cascade. The canvas root sets the dashboard's effective values,
// a tile wrapper the block's own, and each role's drawing element reads
// `var(--dash-<attr>-<role>, <its own value today>)`, so an unset variable is
// exactly today's look. Every value is a lookup in a closed set below.

/** The closed sets, equal to `api/constants/dashboard_text_styles.py` (a test). */
export const FONT_WEIGHTS = [400, 500, 600, 700] as const;
export const TEXT_ALIGNS = ["left", "center", "right"] as const;
export const LETTER_CASES = ["none", "upper"] as const;
export const LINE_SPACINGS = ["compact", "normal", "relaxed"] as const;
/** The roles each attribute is drawn on (D5); every role draws the others. */
export const ALIGN_ROLES: readonly FontRole[] = ["title", "text"];
/** Strikethrough: headings, text and smart text (amendment A2). */
export const STRIKE_ROLES: readonly FontRole[] = ["title", "text"];
export const CASE_ROLES: readonly FontRole[] = ["title", "chart_title", "chart_subtitle", "table_header", "control_label"];
/** Every role but a control's values, whose colour is per state (the
 *  backend's `COLOR_ROLES`). */
export const COLOR_ROLES: readonly FontRole[] = FONT_ROLES.filter((r) => r !== "control_value");
export const LINE_SPACING_ROLES: readonly FontRole[] = ["text"];
/** The line height each spacing draws with; "normal" is today's 1.6. */
export const LINE_HEIGHT: Readonly<Record<(typeof LINE_SPACINGS)[number], string>> = {
  compact: "1.3",
  normal: "1.6",
  relaxed: "1.9",
};

/** One role's weight/italic/underline/align/case/spacing, re-checked. */
export type RoleStyle = {
  weight?: (typeof FONT_WEIGHTS)[number];
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  align?: (typeof TEXT_ALIGNS)[number];
  letter_case?: (typeof LETTER_CASES)[number];
  line_spacing?: (typeof LINE_SPACINGS)[number];
};
/** Per role (absent = the level above / today's). */
export type DashboardRoleStyles = Partial<Record<FontRole, RoleStyle>>;

const includes = <T,>(set: readonly T[], v: unknown): v is T => (set as readonly unknown[]).includes(v);

/** One role's values from a stored map, each checked against its set and
 *  its roles; anything else is dropped. */
export function storedRoleStyle(role: FontRole, raw: Record<string, unknown> | null | undefined): RoleStyle {
  const out: RoleStyle = {};
  if (!raw) return out;
  if (includes(FONT_WEIGHTS, raw.weight)) out.weight = raw.weight;
  if (typeof raw.italic === "boolean") out.italic = raw.italic;
  if (typeof raw.underline === "boolean") out.underline = raw.underline;
  if (STRIKE_ROLES.includes(role) && typeof raw.strikethrough === "boolean") out.strikethrough = raw.strikethrough;
  if (ALIGN_ROLES.includes(role) && includes(TEXT_ALIGNS, raw.align)) out.align = raw.align;
  if (CASE_ROLES.includes(role) && includes(LETTER_CASES, raw.letter_case)) out.letter_case = raw.letter_case;
  if (LINE_SPACING_ROLES.includes(role) && includes(LINE_SPACINGS, raw.line_spacing)) out.line_spacing = raw.line_spacing;
  return out;
}

/** The role styles a level sets, from its `text_styles`. */
export function itemRoleStyles(item: ItemTextStyles | null | undefined): DashboardRoleStyles {
  const out: DashboardRoleStyles = {};
  for (const role of FONT_ROLES) {
    const style = storedRoleStyle(role, item?.text_styles?.[role]);
    if (Object.keys(style).length) out[role] = style;
  }
  return out;
}

const dashes = (role: FontRole) => role.replace(/_/g, "-");
export const weightVar = (role: FontRole) => `--dash-weight-${dashes(role)}` as const;
export const styleVar = (role: FontRole) => `--dash-style-${dashes(role)}` as const;
export const decorationVar = (role: FontRole) => `--dash-decoration-${dashes(role)}` as const;
export const alignVar = (role: FontRole) => `--dash-align-${dashes(role)}` as const;
export const caseVar = (role: FontRole) => `--dash-case-${dashes(role)}` as const;
export const LEADING_VAR = "--dash-leading-text";


/** The variables to set for the given role styles; an absent value sets
 *  nothing. `above` is the level above's (a block's dashboard): underline and
 *  strikethrough share one CSS property, so a level that sets one of them
 *  writes the line with the other as the level above has it. */
export function roleStyleVariables(
  styles: DashboardRoleStyles | null | undefined,
  above: DashboardRoleStyles | null | undefined = null,
): CSSProperties {
  const out: Record<string, string> = {};
  if (!styles) return out;
  for (const role of FONT_ROLES) {
    const s = storedRoleStyle(role, styles[role] as Record<string, unknown> | undefined);
    const up = storedRoleStyle(role, above?.[role] as Record<string, unknown> | undefined);
    if (s.weight !== undefined) out[weightVar(role)] = String(s.weight);
    if (s.italic !== undefined) out[styleVar(role)] = s.italic ? "italic" : "normal";
    if (s.underline !== undefined || s.strikethrough !== undefined) {
      const underline = s.underline ?? up.underline ?? false;
      const strike = s.strikethrough ?? up.strikethrough ?? false;
      const lines = [underline ? "underline" : "", strike ? "line-through" : ""].filter(Boolean).join(" ");
      out[decorationVar(role)] = lines || "none";
    }
    if (s.align !== undefined) out[alignVar(role)] = s.align;
    if (s.letter_case !== undefined) out[caseVar(role)] = s.letter_case === "upper" ? "uppercase" : "none";
    if (s.line_spacing !== undefined) out[LEADING_VAR] = LINE_HEIGHT[s.line_spacing];
  }
  return out;
}

/** The style properties a role's DRAWING element reads, each falling back to
 *  what the element draws today. `weight` is today's weight (a number or a
 *  `var(...)`, e.g. `mediumFontWeight(role)`); pass `align`/`textCase` only
 *  where the role draws them. */
export function roleTextStyle(
  role: FontRole,
  today: { weight?: string | number; align?: string; textCase?: string } = {},
): CSSProperties {
  const style: Record<string, string> = {
    fontStyle: `var(${styleVar(role)}, normal)`,
    textDecorationLine: `var(${decorationVar(role)}, none)`,
  };
  if (today.weight !== undefined) style.fontWeight = `var(${weightVar(role)}, ${today.weight})`;
  if (ALIGN_ROLES.includes(role) && today.align !== undefined) style.textAlign = `var(${alignVar(role)}, ${today.align})`;
  if (CASE_ROLES.includes(role) && today.textCase !== undefined) style.textTransform = `var(${caseVar(role)}, ${today.textCase})`;
  return style as CSSProperties;
}

/** The effective role styles for one block: its own over the dashboard's
 *  (the CSS cascade's result, for the KPI value, which is sized in JS). */
export function effectiveRoleStyles(dashboard: DashboardRoleStyles | null | undefined, item: ItemTextStyles | null | undefined): DashboardRoleStyles {
  const own = itemRoleStyles(item);
  const out: DashboardRoleStyles = {};
  for (const role of FONT_ROLES) {
    const merged = { ...storedRoleStyle(role, dashboard?.[role] as Record<string, unknown> | undefined), ...own[role] };
    if (Object.keys(merged).length) out[role] = merged;
  }
  return out;
}

/** A DTO's `text_styles` split into the maps the canvas and the editors use. */
export function splitTextStyles(styles: ItemTextStyles["text_styles"]): {
  fonts: DashboardFonts;
  colors: DashboardFontColors;
  sizes: DashboardFontSizes;
  roleStyles: DashboardRoleStyles;
} {
  const item = { text_styles: styles };
  return { fonts: itemFonts(item), colors: itemFontColors(item), sizes: itemFontSizes(item), roleStyles: itemRoleStyles(item) };
}

// --- what a role draws when nothing is set (plan 2026-10-17-dashboard-text-
// style-complete, D2) --------------------------------------------------------
//
// The editors show the value in effect at every level, so the top level, the
// platform, needs real values: today's look per role. The lab gate measures
// every role's computed style with nothing set against this table, so it
// cannot drift from the renderers. `font: null` is the app's body font, which
// is not a catalogue font (and not loaded as a web font): "System font".

export type PlatformRoleStyle = {
  font: string | null;
  color: "text" | "muted";
  size: number;
  weight: (typeof FONT_WEIGHTS)[number];
  italic: false;
  underline: false;
  strikethrough?: false;
  align?: "left";
  letter_case?: "none";
  line_spacing?: "normal";
};

const plain = { italic: false, underline: false } as const;

export const PLATFORM_TEXT_STYLE: Readonly<Record<FontRole, PlatformRoleStyle>> = {
  title: { ...plain, size: 22, font: "inter", color: "text", weight: 700, strikethrough: false, align: "left", letter_case: "none" },
  text: { ...plain, size: 14, font: null, color: "text", weight: 400, strikethrough: false, align: "left", line_spacing: "normal" },
  // Medium: 500, drawn 600 where the family has no 500 face (`mediumFontWeight`).
  chart_title: { ...plain, size: 14, font: null, color: "text", weight: 500, letter_case: "none" },
  chart_subtitle: { ...plain, size: 12, font: null, color: "muted", weight: 400, letter_case: "none" },
  table_header: { ...plain, size: 16, font: null, color: "muted", weight: 600, letter_case: "none" },
  table_body: { ...plain, size: 13, font: null, color: "text", weight: 400 },
  kpi_value: { ...plain, size: 32, font: null, color: "text", weight: 700 },
  // The label as `Labelled` draws it (`Text size="sm" fw={500}`); the values
  // as a value button draws them (Mantine `Button size="xs"`: 12 px, 600).
  control_label: { ...plain, size: 14, font: null, color: "text", weight: 500, letter_case: "none" },
  control_value: { ...plain, size: 12, font: null, color: "text", weight: 600 },
};
